import { useCallback, useEffect, useRef, useState } from "react";

interface Options {
  enabled: boolean;
  onCommit: () => void;
  edgeZone?: number;
  directionLock?: number;
  commitFraction?: number;
}

interface Result {
  offset: number;
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
    onTouchCancel: (e: React.TouchEvent) => void;
  };
}

/**
 * Native-style horizontal edge-swipe to go back — mirrors iOS / Telegram behaviour.
 *
 * Constrained to the first `edgeZone` px so it doesn't collide with in-chat
 * horizontal gestures (swipe-to-reply on bubbles, horizontal scroll inside
 * attachments).
 */
export default function useSwipeBack({
  enabled,
  onCommit,
  edgeZone = 28,
  directionLock = 12,
  commitFraction = 0.35,
}: Options): Result {
  const [offset, setOffset] = useState(0);
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const locked = useRef<"horizontal" | "vertical" | null>(null);
  const offsetRef = useRef(0);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    if (enabled) return;
    if (offsetRef.current !== 0) setOffset(0);
    offsetRef.current = 0;
    startX.current = null;
    startY.current = null;
    locked.current = null;
  }, [enabled]);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      if (!touch || touch.clientX > edgeZone) return;
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      locked.current = null;
      offsetRef.current = 0;
    },
    [enabled, edgeZone]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (startX.current === null || startY.current === null) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;

      if (!locked.current && (Math.abs(dx) > directionLock || Math.abs(dy) > directionLock)) {
        locked.current = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
      }

      if (locked.current !== "horizontal" || dx <= 0) return;

      const next = Math.min(dx, window.innerWidth);
      if (next === offsetRef.current) return;
      offsetRef.current = next;
      setOffset(next);
    },
    [directionLock]
  );

  const finish = useCallback(() => {
    const dx = offsetRef.current;
    const commit = locked.current === "horizontal" && dx >= window.innerWidth * commitFraction;

    if (offsetRef.current !== 0) setOffset(0);
    offsetRef.current = 0;
    startX.current = null;
    startY.current = null;
    locked.current = null;

    if (commit) onCommitRef.current();
  }, [commitFraction]);

  return {
    offset,
    handlers: { onTouchStart, onTouchMove, onTouchEnd: finish, onTouchCancel: finish },
  };
}
